import { useRef } from 'react';

declare global {
  interface Window {
    // TO DO: This will be updated with GQL changes
    braintree?: any;
  }
}

const SCRIPTS = [
  { id: 'braintree-client', url: 'https://js.braintreegateway.com/web/3.81.0/js/client.min.js' },
  {
    id: 'braintree-collector',
    url: 'https://js.braintreegateway.com/web/3.81.0/js/data-collector.min.js',
  },
];

function loadScript(id: string, url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const exists = document.getElementById(id);
    if (exists) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = url;
    el.id = id;
    el.onload = () => resolve();
    el.onerror = () => reject();
    document.body.appendChild(el);
  });
}

async function embedScripts(): Promise<void> {
  for (const script of SCRIPTS) {
    await loadScript(script.id, script.url);
  }
}

function createBraintreeClient(braintree: any, token: string): Promise<void> {
  return new Promise((resolve, reject) => {
    braintree.client.create(
      {
        authorization: token,
      },
      (err: any, clientInstance: any) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(clientInstance);
      }
    );
  });
}

function createDataCollector(braintree: any, client: any): Promise<string> {
  return new Promise((resolve, reject) => {
    braintree.dataCollector.create(
      {
        client,
      },
      (err: any, dataCollectorInstance: any) => {
        if (err) {
          reject(err);
          return;
        }
        // At this point, you should access the dataCollectorInstance.deviceData value and provide it
        // to your server, e.g. by injecting it into your form as a hidden input.
        resolve(dataCollectorInstance.deviceData);
      }
    );
  });
}

async function resolveDeviceToken(braintree: any, token: string): Promise<string> {
  const client = await createBraintreeClient(braintree, token);
  const deviceToken = await createDataCollector(braintree, client);
  return deviceToken;
}

export const useBraintree = () => {
  const deviceTokenRef = useRef<string | null>(null);

  async function getDeviceToken(tokenizationKey: string) {
    if (!deviceTokenRef.current) {
      if (!window.braintree) {
        await embedScripts();
      }

      if (!window.braintree) {
        throw new Error('Braintree script did not load properly');
      }

      deviceTokenRef.current = await resolveDeviceToken(window.braintree, tokenizationKey);
    }

    return deviceTokenRef.current;
  }

  return {
    getDeviceToken,
  };
};
